'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { Bed, Bath, MapPin, Home, Heart, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ImageLoader from '../ui/image-loader';

export default function PropertyGrid({
  properties,
  totalProperties,
  currentPage,
  itemsPerPage,
  onPageChange,
  offeringType,
  viewMode,
}) {
  const [savedProperties, setSavedProperties] = useState([]);

  // Share property
  const shareProperty = async (property) => {
    const shareData = {
      title: property.title_en,
      text: `Check out this property: ${property.title_en} in ${property.community}`,
      url: `${window.location.origin}/properties/${offeringType}/${property.slug}`,
    };

    try {
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare(shareData)
      ) {
        await navigator.share(shareData);
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareData.url);
        alert('Property link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareData.url);
        alert('Property link copied to clipboard!');
      } catch (clipboardError) {
        console.error('Clipboard error:', clipboardError);
        alert(`Share this property: ${shareData.url}`);
      }
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalProperties / itemsPerPage);

  // Property type mapping
  const getPropertyTypeLabel = (type) => {
    const typeMap = {
      AP: 'Apartment',
      VH: 'Villa',
      TH: 'Townhouse',
      PH: 'Penthouse',
      LP: 'Land',
      OF: 'Office',
      RE: 'Retail',
      SH: 'Shop',
      WH: 'Warehouse',
      FA: 'Factory',
      SR: 'Show Room',
      BW: 'Bungalow',
      CD: 'Compound',
      DX: 'Duplex',
      FF: 'Full Floor',
      HF: 'Half Floor',
      LC: 'Labor Camp',
      WB: 'Whole Building',
    };
    return typeMap[type] || 'Property';
  };

  // Offering type label
  const getOfferingTypeLabel = (type) => {
    const typeMap = {
      RS: 'For Sale',
      CS: 'For Sale',
      RR: 'For Rent',
      CR: 'For Rent',
    };
    return typeMap[type] || 'For Sale';
  };

  // Format price
  const formatPrice = (price, offeringType, rentalPeriod) => {
    const formattedPrice = Number(price).toLocaleString();

    if (offeringType === 'RR' || offeringType === 'CR') {
      const periodMap = {
        Y: '/year',
        M: '/month',
        W: '/week',
        D: '/day',
      };
      return `AED ${formattedPrice}${periodMap[rentalPeriod] || ''}`;
    }

    return `AED ${formattedPrice}`;
  };

  const updateSavedProperties = (newSaved) => {
    localStorage.setItem('xspace_saved_properties', JSON.stringify(newSaved));
    window.dispatchEvent(new Event('xspace_saved_updated'));
  };

  // Toggle bookmark
  const toggleSaveProperty = (property) => {
    const savedProperties = JSON.parse(
      localStorage.getItem('xspace_saved_properties') || '[]'
    );

    let updatedSaved;

    if (savedProperties.find((p) => p.slug === property.slug)) {
      updatedSaved = savedProperties.filter((p) => p.slug !== property.slug);
    } else {
      updatedSaved = [...savedProperties, property];
    }

    updateSavedProperties(updatedSaved);

    const isPropertySaved = savedProperties.some(
      (saved) => saved?.slug === property?.slug
    );

    if (isPropertySaved) {
      // Remove bookmark
      const updatedSavedProperties = savedProperties.filter(
        (saved) => saved?.slug !== property?.slug
      );

      localStorage.setItem(
        'xspace_saved_properties',
        JSON.stringify(updatedSavedProperties)
      );
      setSavedProperties(updatedSavedProperties);
      window.dispatchEvent(new Event('xspace_saved_properties'));
    } else {
      // Add bookmark
      const newPropertyData = {
        id: property._id || property.reference_number,
        title: property.title_en,
        location: property.community
          ? `${property.community}, ${property.sub_community}`
          : property.sub_community,
        price: property.price,
        image: property.photo_url || '/placeholder.svg',
        bedroom: property.bedroom,
        bathroom: property.bathroom,
        size: property.size,
        property_type: property.property_type,
        offering_type: property.offering_type,
        rental_period: property.rental_period,
        slug: property.slug,
        community: property.community,
        sub_community: property.sub_community,
        permit_number: property.permit_number?.[0],
        completion_status: property.completion_status?.[0],
        url: `/properties/${offeringType}/${property?.slug}`,
        savedAt: new Date().toISOString(),
      };

      const updatedSavedProperties = [...savedProperties, newPropertyData];
      localStorage.setItem(
        'xspace_saved_properties',
        JSON.stringify(updatedSavedProperties)
      );
      setSavedProperties(updatedSavedProperties);

      window.dispatchEvent(new Event('xspace_saved_properties'));
    }
  };

  useEffect(() => {
    // Load Properties from localStorage
    const properties = JSON.parse(
      localStorage.getItem('xspace_saved_properties') || '[]'
    );

    setSavedProperties(properties);
  }, []);

  const isPropertySaved = (slug) => {
    return savedProperties.some((saved) => saved.slug === slug);
  };

  // Render grid view
  const renderGridView = () => (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6'>
      <AnimatePresence>
        {properties.map((property, index) => {
          const propertyId = property._id;
          const propertySlug = property?.slug;
          const isSaved = isPropertySaved(propertySlug);

          return (
            <motion.div
              key={propertyId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className='h-full'
            >
              <div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 relative group'>
                {/* Property Image */}
                <div className='relative h-56 overflow-hidden rounded-xl'>
                  <Link
                    rel='preload'
                    href={`/properties/${offeringType}/${property.slug}`}
                  >
                    {/* <Image
                      src={
                        property.photo?.url?.[0]?._ ||
                        '/placeholder.svg?height=400&width=600'
                      }
                      alt={property.title_en || 'Property'}
                      fill
                      className='object-cover transition-transform duration-500 hover:scale-110'
                      sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
                      unoptimized
                    /> */}

                    <ImageLoader
                      src={property.photo_url || '/placeholder.svg'}
                      alt={property.title_en?.[0] || 'Property'}
                      fill
                      className='transition-transform duration-500 hover:scale-110'
                      loaderType='hash'
                      objectFit='cover'
                      priority={index === 0}
                    />
                  </Link>
                  <div className='absolute top-0 left-0 right-0 flex justify-between items-center p-3 bg-gradient-to-b from-black/70 to-transparent'>
                    <span className='text-white text-sm font-medium px-3 py-1 rounded-full bg-primarycolor/90'>
                      {getOfferingTypeLabel(property.offering_type?.[0])}
                    </span>
                    <span className='text-white text-sm font-medium'>
                      Ref: {property.reference_number?.[0]}
                    </span>
                  </div>

                  {/* Save and Share Buttons */}
                  <div className='absolute bottom-3 right-3 flex gap-2 z-10'>
                    <Button
                      variant='secondary'
                      size='icon'
                      className='h-8 w-8 bg-white/95 hover:bg-white shadow-lg border border-gray-200'
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        shareProperty(property);
                      }}
                      aria-label='Share property'
                    >
                      <Share2
                        size={14}
                        className='text-gray-700 dark:text-gray-300'
                      />
                    </Button>
                    <Button
                      variant='secondary'
                      size='icon'
                      // className={`h-8 w-8 shadow-lg border transition-all duration-200 ${
                      //   isSaved
                      //     ? 'bg-red-500 hover:bg-red-600 text-white border-red-500'
                      //     : 'bg-white/95 hover:bg-white text-gray-700 border-gray-200'
                      // }`}
                      className={
                        'h-8 w-8 shadow-lg border transition-all duration-200'
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSaveProperty(property);
                      }}
                      aria-label={
                        isPropertySaved(property.slug)
                          ? 'Remove from saved'
                          : 'Save property'
                      }
                    >
                      {isPropertySaved(property.slug) ? (
                        <Heart
                          size={16}
                          className='text-[#FE2A53] fill-current'
                        />
                      ) : (
                        <Heart
                          size={14}
                          className={isSaved ? 'fill-current' : ''}
                        />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Property Details */}
                <Link
                  rel='preload'
                  href={`/properties/${offeringType}/${property.slug}`}
                >
                  <div className='p-4'>
                    <div className='mb-2'>
                      <div className='flex justify-between'>
                        <h3 className='text-lg font-bold text-gray-900 dark:text-white line-clamp-1'>
                          {property.title_en?.[0]}
                        </h3>
                      </div>
                      <div className='flex justify-between'>
                        <p className='text-primarycolor dark:text-secondarycolor font-bold'>
                          {formatPrice(
                            property.price?.[0],
                            property.offering_type?.[0],
                            property.rental_period?.[0]
                          )}
                        </p>
                      </div>
                    </div>

                    <div className='flex items-center text-gray-600 dark:text-gray-400 mb-3'>
                      <MapPin size={16} className='mr-1' />
                      <span className='text-sm line-clamp-1'>
                        {property.sub_community?.[0]
                          ? `${property.sub_community?.[0]}, ${property.community?.[0]}`
                          : property.community?.[0]}
                      </span>
                    </div>

                    <div className='flex items-center justify-between text-sm text-gray-700 dark:text-gray-300 mb-3'>
                      <span className='flex items-center'>
                        <Home size={16} className='mr-1' />
                        {getPropertyTypeLabel(property.property_type?.[0])}
                      </span>
                      <span className='flex items-center'>
                        <Bed size={16} className='mr-1' />
                        {property.bedroom?.[0] || '0'}{' '}
                        {property.bedroom?.[0] === '1' ? 'Bed' : 'Beds'}
                      </span>
                      <span className='flex items-center'>
                        <Bath size={16} className='mr-1' />
                        {property.bathroom?.[0] || '0'}{' '}
                        {property.bathroom?.[0] === '1' ? 'Bath' : 'Baths'}
                      </span>
                    </div>

                    <div className='text-sm text-gray-600 dark:text-gray-400 line-clamp-2 h-10'>
                      {property.description_en?.[0]}
                    </div>
                  </div>
                </Link>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );

  // Render list view
  const renderListView = () => (
    <div className='space-y-4'>
      <AnimatePresence>
        {properties.map((property, index) => {
          const propertyId = property._id;
          const propertySlug = property.slug;
          const isSaved = isPropertySaved(propertySlug);

          return (
            <motion.div
              key={propertyId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 relative group'>
                <div className='flex flex-col md:flex-row'>
                  {/* Property Image */}
                  <div className='relative h-56 md:h-full md:w-1/3 md:min-h-[220px] overflow-hidden'>
                    <Link
                      rel='preload'
                      href={`/properties/${offeringType}/${property.slug}`}
                    >
                      <ImageLoader
                        src={property.photo_url || '/placeholder.svg'}
                        alt={property.title_en?.[0] || 'Property'}
                        fill
                        className='transition-transform duration-500 hover:scale-110'
                        loaderType='hash'
                        objectFit='cover'
                        priority={index === 0}
                      />
                    </Link>
                    <div className='absolute top-0 left-0 right-0 flex justify-between items-center p-3 bg-gradient-to-b from-black/70 to-transparent'>
                      <span className='text-white text-sm font-medium px-3 py-1 rounded-full bg-primarycolor/90'>
                        {getOfferingTypeLabel(property.offering_type?.[0])}
                      </span>
                      <span className='text-white text-sm font-medium'>
                        Ref: {property.reference_number?.[0]}
                      </span>
                    </div>

                    {/* Save and Share Buttons */}
                    <div className='absolute top-3 right-3 flex gap-2 z-10'>
                      <Button
                        variant='secondary'
                        size='icon'
                        className='h-8 w-8 bg-white/95 hover:bg-white shadow-lg border border-gray-200'
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          shareProperty(property);
                        }}
                        aria-label='Share property'
                      >
                        <Share2
                          size={14}
                          className='text-gray-700 dark:text-gray-300'
                        />
                      </Button>
                      <Button
                        variant='secondary'
                        size='icon'
                        // className={`h-8 w-8 shadow-lg border transition-all duration-200 ${
                        //   isSaved
                        //     ? 'bg-red-500 hover:bg-red-600 text-white border-red-500'
                        //     : 'bg-white/95 hover:bg-white text-gray-700 border-gray-200'
                        // }`}
                        className={
                          'h-8 w-8 shadow-lg border transition-all duration-200'
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleSaveProperty(property);
                        }}
                        aria-label={
                          isPropertySaved(property.slug)
                            ? 'Remove from saved'
                            : 'Save property'
                        }
                      >
                        {isPropertySaved(property.slug) ? (
                          <Heart
                            size={16}
                            className='text-[#FE2A53] fill-current'
                          />
                        ) : (
                          <Heart
                            size={14}
                            className={isSaved ? 'fill-current' : ''}
                          />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Property Details */}
                  <Link
                    rel='preload'
                    href={`/properties/${offeringType}/${property.slug}`}
                    className='md:w-2/3'
                  >
                    <div className='p-4 md:p-6 md:w-2/3'>
                      {/* <div className='flex flex-col md:flex-row justify-between items-start mb-2'>
                        <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2 md:mb-0'>
                          {property.title_en?.[0]}
                        </h3>
                        <span className='text-primarycolor dark:text-secondarycolor font-bold text-lg'>
                          {formatPrice(
                            property.price?.[0],
                            property.offering_type?.[0],
                            property.rental_period?.[0]
                          )}
                        </span>
                      </div> */}

                      <div className='flex flex-col justify-start items-start mb-2'>
                        <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-1'>
                          {property.title_en?.[0]}
                        </h3>
                        <span className='text-primarycolor dark:text-secondarycolor font-bold text-lg'>
                          {formatPrice(
                            property.price?.[0],
                            property.offering_type?.[0],
                            property.rental_period?.[0]
                          )}
                        </span>
                      </div>

                      <div className='flex items-center text-gray-600 dark:text-gray-400 mb-3'>
                        <MapPin size={16} className='mr-1' />
                        <span className='text-sm'>
                          {property.sub_community?.[0]
                            ? `${property.sub_community?.[0]}, ${property.community?.[0]}`
                            : property.community?.[0]}
                        </span>
                      </div>

                      <div className='flex flex-wrap gap-4 text-sm text-gray-700 dark:text-gray-300 mb-3'>
                        <span className='flex items-center'>
                          <Home size={16} className='mr-1' />
                          {getPropertyTypeLabel(property.property_type?.[0])}
                        </span>
                        <span className='flex items-center'>
                          <Bed size={16} className='mr-1' />
                          {property.bedroom?.[0] || '0'}{' '}
                          {property.bedroom?.[0] === '1' ? 'Bed' : 'Beds'}
                        </span>
                        <span className='flex items-center'>
                          <Bath size={16} className='mr-1' />
                          {property.bathroom?.[0] || '0'}{' '}
                          {property.bathroom?.[0] === '1' ? 'Bath' : 'Baths'}
                        </span>
                        {property.size?.[0] && (
                          <span className='flex items-center'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='h-4 w-4 mr-1'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5'
                              />
                            </svg>
                            {property.size?.[0]} sqft
                          </span>
                        )}
                      </div>

                      <div className='text-sm text-gray-600 dark:text-gray-400 line-clamp-3'>
                        {property.description_en?.[0]}
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );

  return (
    <div>
      {properties?.length === 0 ? (
        <div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <Home className='w-16 h-16 text-gray-400' />
          </div>
          <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
            No properties found
          </h3>
          <p className='text-gray-600 dark:text-gray-400 mb-4'>
            Try adjusting your filters to find properties that match your
            criteria.
          </p>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? renderGridView() : renderListView()}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className='mt-8 flex justify-center'>
              <div className='flex flex-wrap gap-2'>
                {/* Previous Page Button */}
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg ${
                    currentPage === 1
                      ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => {
                    // Show first page, last page, current page, and pages around current page
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => onPageChange(page)}
                          className={`min-w-[40px] px-3 py-2 rounded-lg ${
                            currentPage === page
                              ? 'bg-primarycolor dark:bg-secondarycolor text-white dark:text-black font-medium'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }

                    // Show ellipsis
                    if (
                      (page === 2 && currentPage > 3) ||
                      (page === totalPages - 1 && currentPage < totalPages - 2)
                    ) {
                      return (
                        <span
                          key={page}
                          className='px-3 py-2 text-gray-500 dark:text-gray-400'
                        >
                          ...
                        </span>
                      );
                    }

                    return null;
                  }
                )}

                {/* Next Page Button */}
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 rounded-lg ${
                    currentPage === totalPages
                      ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
